import { useEffect, useMemo, useState } from 'react';

import {
  useListCampaignPeers,
  useShowConsumerCampaignById,
  useShowConsumerCampaignSettingsById,
} from '@givebacks/api/store';
import { toTitleCase } from 'core/Utils';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  ButtonMantine,
  Flex,
  Grid,
  Loader,
  Modal,
  ScrollArea,
  SegmentedControl,
  Skeleton,
  Stack,
  Text,
  TextInput,
  TransitionElement,
} from 'ui';
import { useDebouncedState, useTimeout } from 'ui/Hooks';
import { IconSearch } from 'ui/Icons';

import { useCrowdfundingProvider, useDonorTranslations } from '../../../Context';
import { PeerTypes } from '../../../api/new/types';
import { CampaignPeerInfoCard } from '../Cards';

type CampaignPeerSearchModalProps = {
  type: PeerTypes;
  opened: boolean;
  onClose: () => void;
  teamName?: string;
  togglePeerTypes?: boolean;
  peerPath?: string;
};

interface PeerSearchProps extends Omit<CampaignPeerSearchModalProps, 'onClose' | 'opened'> {}

function PeerSearch({ type, teamName, togglePeerTypes, peerPath }: PeerSearchProps) {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useDebouncedState<string>('', 500);
  const [delayedLoading, setDelayedLoading] = useState(true);
  const { start, clear } = useTimeout(() => setDelayedLoading(false), 500);
  const { t } = useDonorTranslations();
  const { causeId, campaignId } = useCrowdfundingProvider();
  const { data: campaign } = useShowConsumerCampaignById({ causeId, campaignId });

  const [athonParticipantSort, setAthonParticipantSort] = useState<'rank' | 'activity'>('rank');

  const { data: campaignSettings } = useShowConsumerCampaignSettingsById({
    causeId,
    campaignId,
  });
  const { data: peerResponse, isLoading } = useListCampaignPeers({
    peer_type: type,
    campaignId: campaignId,
    causeId: causeId,
    queryOptions: {
      searches: {
        name: { value: searchValue, by: 'in' },
        'team.name': { value: teamName },
      },
      order: {
        field: athonParticipantSort,
        direction: athonParticipantSort === 'rank' ? 'asc' : 'desc',
      },
      join: 'AND',
    },
  });

  const data = peerResponse?.[type];
  const isAthon = campaign?.__type === 'athon';

  const noMatchAlert = useMemo(() => {
    if (data) {
      if (data === undefined || data.length < 1) {
        return (
          <Text
            fz="sm"
            fw={500}
            c="cerise"
          >{`No ${type} match your search. Please try again.`}</Text>
        );
      }
    }
  }, [data]);

  useEffect(() => {
    if (delayedLoading === true && isLoading === false) {
      start();
    }

    return () => clear();
  }, [clear, delayedLoading, isLoading, start]);

  return (
    <Stack gap={'lg'} mih={{ base: '466px', md: undefined }}>
      <Flex
        justify={{ base: 'flex-end', md: 'space-between' }}
        align={'center'}
        wrap="wrap"
        pr={{ base: 'md', md: 0 }}
        mt={2}
        gap={8}
      >
        <TextInput
          style={{
            flex: 1,
          }}
          radius={'sm'}
          leftSection={<IconSearch />}
          placeholder={`Search for a ${
            type === 'teams' ? `${t('team').toLowerCase()}` : `${t('participant').toLowerCase()}`
          }`}
          defaultValue={searchValue}
          onChange={(e) => setSearchValue(e.currentTarget.value)}
          rightSection={isLoading ? <Loader size="1rem" /> : undefined}
        />

        {togglePeerTypes &&
          type === 'teams' &&
          campaignSettings?.isParticipantsEnabled &&
          campaignSettings.isParticipantsInCenterEnabled && (
            <ButtonMantine
              size="sm"
              variant="transparent"
              onClick={() => navigate('../participants')}
            >
              {`View ${t('participants')}`}
            </ButtonMantine>
          )}

        {togglePeerTypes &&
          type === 'participants' &&
          campaignSettings?.isTeamsEnabled &&
          campaignSettings?.isTeamsInCenterEnabled && (
            <ButtonMantine size="sm" variant="transparent" onClick={() => navigate('../teams')}>
              {`View ${t('teams')}`}
            </ButtonMantine>
          )}
      </Flex>

      {isAthon && type === 'participants' && (
        <SegmentedControl
          data={[
            { value: 'rank', label: 'Amount Raised' },
            { value: 'activity', label: toTitleCase(campaign.units_of_activity) },
          ]}
          value={athonParticipantSort}
          onChange={(value) => setAthonParticipantSort(value as 'rank' | 'activity')}
          variant="light"
          size="sm"
        />
      )}

      {noMatchAlert}

      <Box mih={212}>
        <TransitionElement
          duration={500}
          keepMounted
          mounted={!isLoading && !delayedLoading}
          transition={'scale-y'}
        >
          <Grid w="100%">
            {data?.map((peer) => {
              return (
                <Grid.Col span={{ base: 12, md: 4 }} key={peer.uuid}>
                  <CampaignPeerInfoCard
                    peer_id={peer.uuid}
                    type={type}
                    causeId={causeId}
                    campaignId={campaignId}
                    onClick={() =>
                      navigate(peerPath ? `${peerPath}/${peer.uuid}` : `./${peer.uuid}`)
                    }
                  />
                </Grid.Col>
              );
            })}
          </Grid>
        </TransitionElement>

        {/* Custom loading state to match CampaignPeerInfoCard sizes */}
        {(isLoading || delayedLoading) && (
          <Grid w="100%">
            {[...Array(3)].map((_, index) => (
              <Grid.Col span={{ base: 12, md: 4 }} key={index}>
                <Skeleton
                  visible
                  h={{
                    base: 94,
                    md: 208,
                  }}
                  w="100%"
                />
              </Grid.Col>
            ))}
          </Grid>
        )}
      </Box>
    </Stack>
  );
}

const CampaignPeerSearchModal = ({
  type,
  opened,
  onClose,
  teamName,
  togglePeerTypes = true,
  peerPath,
}: CampaignPeerSearchModalProps) => {
  const { t } = useDonorTranslations();

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      padding="xl"
      scrollAreaComponent={ScrollArea.Autosize}
      pos="relative"
      title={type === 'teams' ? `${t('teams')}` : `${t('participants')}`}
      styles={{ title: { textTransform: 'capitalize' } }}
      size="lg"
    >
      <PeerSearch
        type={type}
        teamName={teamName}
        togglePeerTypes={togglePeerTypes}
        peerPath={peerPath}
      />
    </Modal>
  );
};

export { CampaignPeerSearchModal };
